// frontend/src/features/onboarding/steps/PolariStep.tsx
// New step — replaces MarketIntelligenceStep
// No API calls needed here; Polari settings come from user account defaults.
// This step is informational + toggle preferences only.
import React, { useState } from 'react'
import { ArrowRight, ArrowLeft, Sparkles } from 'lucide-react'
import { useNavigate } from 'react-router-dom'

interface PolariStepProps {
  eyebrow: string
  accentClass: string
  btnClass: string
  onNext: () => void
  onBack: () => void
}

interface LayerToggle {
  l0_quality: boolean
  l1_embeddings: boolean
  l2_clustering: boolean
}

export function PolariStep({ eyebrow, accentClass, btnClass, onNext, onBack }: PolariStepProps) {
  const navigate = useNavigate()
  const [toggles, setToggles] = useState<LayerToggle>({
    l0_quality: true,
    l1_embeddings: true,
    l2_clustering: true,
  })

  const flip = (key: keyof LayerToggle) =>
    setToggles(prev => ({ ...prev, [key]: !prev[key] }))

  const FREE_LAYERS = [
    {
      key: 'l0_quality' as const,
      badge: 'L0',
      badgeClass: 'lb-l0',
      name: 'Quality scoring',
      desc: 'Quality filter. Threshold: 55.',
    },
    {
      key: 'l1_embeddings' as const,
      badge: 'L1',
      badgeClass: 'lb-l1',
      name: 'Semantic embeddings',
      desc: 'Semantic deduplication and similarity.',
    },
    {
      key: 'l2_clustering' as const,
      badge: 'L2',
      badgeClass: 'lb-l2',
      name: 'Story clustering',
      desc: 'Cross-source story threads.',
    },
  ]

  const GATED_LAYERS = [
    {
      badge: 'L3',
      badgeClass: 'lb-l3',
      name: 'Entity & trend grid',
      desc: 'Entity grid, trend velocity, co-occurrence.',
    },
    {
      badge: 'Echo',
      badgeClass: 'lb-echo',
      name: 'Discourse signals',
      desc: 'Controversy, counter-narratives, sentiment.',
    },
  ]

  return (
    <>
      <div className="ob-body">
        {/* Polari header card */}
        <div style={{
          display: 'flex', alignItems: 'center', gap: 10,
          padding: '10px 13px', borderRadius: 9,
          border: '0.5px solid rgba(255,18,199,0.2)',
          background: 'rgba(255,18,199,0.04)',
          marginBottom: 14, position: 'relative', overflow: 'hidden',
        }}>
          {/* Pink topline */}
          <div style={{
            position: 'absolute', top: 0, left: 0, right: 0, height: 1,
            background: 'linear-gradient(90deg,#FF12C7,rgba(102,0,255,0.6),transparent 65%)',
          }} />
          {/* Polari icon */}
          <div style={{
            width: 28, height: 28, borderRadius: 7, flexShrink: 0,
            background: 'linear-gradient(135deg,rgba(255,18,199,.2),rgba(102,0,255,.15))',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="url(#polg)" strokeWidth="2" aria-hidden="true">
              <defs>
                <linearGradient id="polg" x1="0" y1="0" x2="1" y2="1">
                  <stop offset="0%" stopColor="#FF12C7" />
                  <stop offset="100%" stopColor="#6600FF" />
                </linearGradient>
              </defs>
              <circle cx="12" cy="12" r="3" />
              <path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83" />
            </svg>
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 13, fontWeight: 500 }}>
              <span style={{ color: '#FF12C7' }}>Polari</span> Intelligence
            </div>
            <div style={{ fontSize: 10, color: 'var(--color-text-secondary)', marginTop: 1 }}>
              Multi-layer article enrichment API
            </div>
          </div>
          <span style={{
            fontSize: 9, padding: '2px 7px', borderRadius: 99,
            background: 'rgba(255,18,199,0.08)', border: '0.5px solid rgba(255,18,199,0.25)',
            color: 'rgba(255,18,199,0.8)', fontFamily: 'var(--font-mono, monospace)',
          }}>enterprise</span>
        </div>

        {/* Eyebrow + heading below header card */}
        <div className={`ob-eyebrow ${accentClass}`}>{eyebrow}</div>
        <h2 className="ob-heading">Intelligence pipeline</h2>
        <p className="ob-sub">Article enrichment runs before every briefing.</p>

        {/* Free layers */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 2 }}>
          {FREE_LAYERS.map(layer => (
            <div
              key={layer.key}
              className="ob-card-surface"
              style={{ padding: '10px 13px' }}
            >
              <div className="ob-topline ob-topline--pink" />
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span className={`layer-badge ${layer.badgeClass}`}>{layer.badge}</span>
                <span style={{ fontSize: 12, fontWeight: 500, flex: 1 }}>{layer.name}</span>
                <button
                  type="button"
                  className={`ob-tog${toggles[layer.key] ? '' : ' ob-tog--off'}`}
                  onClick={() => flip(layer.key)}
                  aria-label={`Toggle ${layer.name}`}
                />
              </div>
              <div style={{ fontSize: 10, color: 'var(--color-text-secondary)', marginTop: 4, lineHeight: 1.45 }}>
                {layer.desc}
              </div>
            </div>
          ))}
        </div>

        {/* Divider */}
        <div style={{
          display: 'flex', alignItems: 'center', gap: 8,
          margin: '12px 0 10px',
          fontSize: 10, letterSpacing: '0.08em', textTransform: 'uppercase',
          color: 'rgba(255,18,199,0.45)',
        }}>
          <div style={{ flex: 1, height: 0.5, background: 'rgba(255,18,199,0.15)' }} />
          Pro+ layers
          <div style={{ flex: 1, height: 0.5, background: 'rgba(255,18,199,0.15)' }} />
        </div>

        {/* Gated layers */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 10, opacity: 0.5, pointerEvents: 'none' }}>
          {GATED_LAYERS.map(layer => (
            <div key={layer.badge} className="ob-card-surface" style={{ padding: '10px 13px' }}>
              <div style={{
                position: 'absolute', top: 0, left: 0, right: 0, height: 1,
                background: 'linear-gradient(90deg,rgba(255,18,199,.3),rgba(102,0,255,.2),transparent 65%)',
              }} />
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span className={`layer-badge ${layer.badgeClass}`}>{layer.badge}</span>
                <span style={{ fontSize: 12, fontWeight: 500, flex: 1 }}>{layer.name}</span>
                <span className="ob-pro-pip">PRO+</span>
                <button type="button" className="ob-tog ob-tog--off" disabled aria-label={layer.name + ' locked'} />
              </div>
              <div style={{ fontSize: 10, color: 'var(--color-text-secondary)', marginTop: 4, lineHeight: 1.45 }}>
                {layer.desc}
              </div>
            </div>
          ))}
        </div>

        {/* Gate bar */}
        <div className="ob-gate-bar">
          <Sparkles size={13} color="#FF12C7" style={{ flexShrink: 0 }} />
          <span className="ob-gate-text">L3 entity tracking and Echo discourse signals unlock with Pro+.</span>
          <button className="ob-gate-cta" type="button" onClick={() => navigate('/billing')}>Upgrade →</button>
        </div>
      </div>

      {/* Layer badge styles — scoped here so we don't pollute global */}
      <style>{`
        .layer-badge { font-size: 9px; font-weight: 600; padding: 2px 6px; border-radius: 4px; letter-spacing: .04em; font-family: var(--font-mono, monospace); }
        .lb-l0 { background: rgba(0,255,192,0.12); color: #00c896; border: 0.5px solid rgba(0,255,192,.25); }
        .lb-l1 { background: rgba(100,180,255,0.1); color: rgba(100,180,255,.9); border: 0.5px solid rgba(100,180,255,.2); }
        .lb-l2 { background: rgba(102,0,255,0.1); color: #9966ff; border: 0.5px solid rgba(102,0,255,.22); }
        .lb-l3 { background: rgba(255,18,199,0.1); color: #FF12C7; border: 0.5px solid rgba(255,18,199,.25); }
        .lb-echo { background: rgba(80,180,255,0.1); color: rgba(80,180,255,.85); border: 0.5px solid rgba(80,180,255,.2); }
      `}</style>

      {/* Footer */}
      <div className="ob-footer">
        <button type="button" className="ob-btn-back" onClick={onBack}>
          <ArrowLeft size={13} /> Back
        </button>
        <button type="button" className={`ob-btn-next ${btnClass}`} onClick={onNext}>
          Continue <ArrowRight size={13} />
        </button>
      </div>
    </>
  )
}